import { useAuthorization } from 'dx-sdk/build/authorization';
import { Hero, IconCard, Picture, Typography } from 'dx-sdk/build/components';
import { GettingStarted } from 'dx-sdk/build/icons/illustrations';
import { Theme as ThemeProvider } from 'dx-sdk/build/providers';
import { formatDealerName } from 'pubweb-smokey/dist/utils/formatters';
import React, { useContext, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link, Redirect } from 'react-router-dom';
import App from '../../../App';
import { DXAccountContext } from '../../../contexts/DXAccountContext';
import HouseGraphic from '../../../images/svg/PortalHeroHome.svg';
import Loading from '../../../images/svg/icons/Loading';
import * as dealerService from '../../../services/dealerService';
import * as homesService from '../../../services/homesService';
import { fireSegmentEvent, parseInitialPropsContext } from '../../../utils';
import { findFeaturedHomes } from '../../../utils/utils';
import RecommendedHomes from '../../components/RecommendedHomes/RecommendedHomes';
import {
  findCombinedFilteredHomes,
  findFavoriteHomes,
  findSaleHomes,
  getPlannerContent,
} from '../../utils/PortalUtils';
import DashboardStyles from './Dashboard.styled';

const Dashboard = ({ dealer, homes }) => {
  const { isAuthenticated, isLoading } = useAuthorization();
  const dxAccountContext = useContext(DXAccountContext);
  const favoriteList = dxAccountContext?.state.favorites;
  const favoritesLoaded = dxAccountContext?.state.favoritesLoaded;
  const favoriteHomes = findFavoriteHomes(favoriteList, homes);
  const saleHomes = findSaleHomes(homes);
  const defaultHomes = findFeaturedHomes(homes);
  const filteredAllHomes = findCombinedFilteredHomes(
    dxAccountContext?.state?.account?.profile?.budget,
    dxAccountContext?.state?.account?.profile?.preferredBeds,
    dxAccountContext?.state?.account?.profile?.preferredBaths,
    dxAccountContext?.state?.account?.profile?.likedFeatures,
    homes
  );
  const CTAContent = getPlannerContent(
    dxAccountContext?.state?.account?.plannerProgress
  );

  const homesList = useMemo(() => {
    if (favoritesLoaded && favoriteHomes && favoriteHomes.length > 0) {
      let slicedArray = favoriteHomes.slice(0, 12);
      return slicedArray?.map((item) => {
        return {
          id: item.serialNumber || item.modelNumber,
          retailHomeObject: {
            ...item,
          },
        };
      });
    }
  }, [favoritesLoaded]);

  const homesAllFilteredList = useMemo(() => {
    return filteredAllHomes?.map((item) => {
      return {
        id: item.serialNumber || item.modelNumber,
        retailHomeObject: {
          ...item,
        },
      };
    });
  }, [filteredAllHomes]);

  const saleHomesList = useMemo(() => {
    let shList = saleHomes && saleHomes.length > 0 ? saleHomes : defaultHomes;
    return shList?.map((item) => {
      return {
        id: item.serialNumber || item.modelNumber,
        retailHomeObject: {
          ...item,
        },
      };
    });
  }, [saleHomes, defaultHomes]);

  if (!isAuthenticated && !isLoading) {
    return <Redirect to="/myhome-account-login" />;
  }

  return (
    <ThemeProvider theme={{}}>
      <App dealer={dealer} homes={homes}>
        <DashboardStyles>
          <Helmet>
            <title>
              MyHome Dashboard | {formatDealerName(dealer?.dealerName)}
            </title>
            <meta name="robots" content="noindex" />
          </Helmet>

          {dxAccountContext?.state?.globalLoading ? (
            <div className="loading-screen-panel">
              <Loading width="150px" />
            </div>
          ) : (
            <>
              <Hero
                accent={<Picture src={HouseGraphic} alt="House Graphic" />}
                heading={
                  dxAccountContext.state.account?.firstName
                    ? 'Welcome, ' +
                      dxAccountContext.state.account?.firstName +
                      '!'
                    : 'Welcome!'
                }
                componentsProps={{
                  Container: {
                    style: {
                      maxWidth: '1058px',
                    },
                  },
                }}
                content={
                  <>
                    <Typography
                      variant="intro"
                      componentProps={{
                        style: {
                          margin: '0',
                        },
                      }}
                    >
                      This is your space to dream, browse and learn more about
                      the home buying journey. View recommended homes near you,
                      save your favorites and track your progress. When you’re
                      ready, connect with your local home center expert.
                    </Typography>
                  </>
                }
              >
                {CTAContent?.DashboardCTAContent ? (
                  <IconCard
                    heading={CTAContent.DashboardCTAContent.headline}
                    icon={<GettingStarted />}
                    subheading={CTAContent.DashboardCTAContent.label}
                    components={{
                      Heading: Typography,
                    }}
                    componentsProps={{
                      Heading: {
                        component: 'h3',
                        variant: 'h1',
                      },
                    }}
                  >
                    <Typography>
                      {CTAContent.DashboardCTAContent.content}
                    </Typography>
                    <div className="buttons-wrap">
                      {CTAContent.DashboardCTAContent.buttons?.map(
                        (button, i) => (
                          <Link
                            className={
                              i == 0 ? 'cta-button' : 'cta-button-outline'
                            }
                            to={{
                              pathname: button.link,
                              state: { dealer, homes },
                            }}
                            key={button?.text + i}
                            onClick={() => {
                              fireSegmentEvent('CTA Clicked', {
                                isNav: false,
                                type: 'button',
                                text: button?.text,
                                eventContext: 'portal_dashboard_banner_cta',
                              });
                            }}
                          >
                            {button.text}
                          </Link>
                        )
                      )}
                    </div>
                  </IconCard>
                ) : (
                  <div className="dashboard-panel-loading">
                    <Loading width="80px" />
                  </div>
                )}
              </Hero>
              {favoritesLoaded && homesList && homesList.length > 0 && (
                <RecommendedHomes
                  swiperID="dashboardFavoritesSwiper"
                  heading="Your Favorite Homes"
                  // subHeading="Based on your selected favorites."
                  listItems={homesList}
                  homes={homes}
                  dealer={dealer}
                  retailEventLabel="portal_dashboard_favorite_homes"
                />
              )}
              {favoritesLoaded &&
                homesAllFilteredList &&
                homesAllFilteredList.length > 0 && (
                  <RecommendedHomes
                    swiperID="allFilteredSwiper"
                    subHeading={`Featured Homes at ${formatDealerName(
                      dealer?.dealerName
                    )}`}
                    listItems={homesAllFilteredList}
                    homes={homes}
                    dealer={dealer}
                    retailEventLabel="portal_dashboard_featured_homes"
                  />
                )}
              {favoritesLoaded &&
                (!homesAllFilteredList || homesAllFilteredList.length <= 0) &&
                (!homesList || homesList.length <= 0) &&
                saleHomesList &&
                saleHomesList.length > 0 && (
                  <RecommendedHomes
                    swiperID="saleHomeSwiper"
                    subHeading={`Featured Homes at ${formatDealerName(
                      dealer?.dealerName
                    )}`}
                    listItems={saleHomesList}
                    homes={homes}
                    dealer={dealer}
                    retailEventLabel="portal_dashboard_featured_homes"
                  />
                )}
            </>
          )}
        </DashboardStyles>
      </App>
    </ThemeProvider>
  );
};

Dashboard.getInitialProps = async (ctx) => {
  return await parseInitialPropsContext(ctx, dealerService, homesService).catch(
    (e) => {
      console.error(e);
      return { redirectTo: '/error' };
    }
  );
};

export default Dashboard;
